import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import MiniStatistics from 'components/card/MiniStatistics';
import { MdContentCopy, MdCheck, MdOutlinePeopleOutline, MdOutlineEmojiEvents, MdArrowForward } from 'react-icons/md';
import { TbCoins } from 'react-icons/tb';
import { TbBrandWhatsapp, TbBrandLinkedin, TbBrandTwitter, TbMail } from 'react-icons/tb';

interface CommissionSummary {
    userCount: number;
    leadCount: number;
    totalDue: number;
}

interface CommissionConfig {
    level1: number;
    level2: number;
}

interface ReferralRow {
    _id: string;
    name: string;
    email: string;
    leadCount: number;
    pricePerLead: number;
    totalDue: number;
}

const formatEuro = (n: number) => `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const BuyerAffiliate = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [userLoading, setUserLoading] = useState(true);
    const [stats, setStats] = useState<CommissionSummary | null>(null);
    const [level2Stats, setLevel2Stats] = useState<CommissionSummary | null>(null);
    const [affiliates, setAffiliates] = useState<ReferralRow[]>([]);
    const [level2Affiliates, setLevel2Affiliates] = useState<ReferralRow[]>([]);
    const [commissionConfig, setCommissionConfig] = useState<CommissionConfig>({ level1: 0, level2: 0 });
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [levelView, setLevelView] = useState<'n1' | 'n2'>('n1');
    const [walletBalance, setWalletBalance] = useState<number>(0);
    const [pendingRequest, setPendingRequest] = useState<{ requestedAt: string; amount: number } | null>(null);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [paymentError, setPaymentError] = useState<string | null>(null);

    const authHeaders = () => ({
        headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('token'),
        },
    });

    const initUser = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/me`, authHeaders())
            .then((res) => {
                setUser(res.data);
                if (res.data && res.data.type !== 'buyer') {
                    window.location.href = '/admin/dashboard';
                }
            })
            .catch(() => {})
            .finally(() => setUserLoading(false));
    };

    const initStats = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/buyer-affiliate-stats`, authHeaders())
            .then((res) => {
                setStats(res.data?.summary ?? null);
                setLevel2Stats(res.data?.level2Summary ?? null);
                setAffiliates(res.data?.affiliates ?? []);
                setLevel2Affiliates(res.data?.level2Affiliates ?? []);
                if (res.data?.config) setCommissionConfig(res.data.config);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const initPaymentStatus = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/affiliate-payment-status`, authHeaders())
            .then((res) => {
                setWalletBalance(res.data.walletBalance ?? 0);
                setPendingRequest(res.data.pendingRequest ?? null);
            })
            .catch(() => {});
    };

    const requestPayment = () => {
        setPaymentLoading(true);
        setPaymentError(null);
        axios
            .post(`${CONFIG.apiUrl}/user/affiliate-request-payment`, {}, authHeaders())
            .then((res) => {
                setPendingRequest({ requestedAt: new Date().toISOString(), amount: res.data.amount });
            })
            .catch((err) => setPaymentError(err?.response?.data?.error || 'Erreur lors de la demande de virement.'))
            .finally(() => setPaymentLoading(false));
    };

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token').length < 10) {
            window.location.href = '/auth/sign-in';
        }
        initUser();
        initStats();
        initPaymentStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const affiliateLink = user?.affiliateCode
        ? `https://app.leadrs.co/auth/sign-up?ref=${user.affiliateCode}`
        : null;
    const hasIban = !!(user?.iban && user.iban.trim());

    const handleCopy = () => {
        if (!affiliateLink) return;
        navigator.clipboard.writeText(affiliateLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const totalLeads = (stats?.leadCount ?? 0) + (level2Stats?.leadCount ?? 0);
    const totalDue = (stats?.totalDue ?? 0) + (level2Stats?.totalDue ?? 0);
    const rows = levelView === 'n1' ? affiliates : level2Affiliates;

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="relative overflow-hidden rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="relative z-10">
                    <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                        Programme de parrainage
                    </p>
                    <h1 className="mt-1 font-crimson text-4xl font-semibold leading-tight text-leadrs-text">
                        Parrainez d'autres acheteurs
                    </h1>
                    <p className="mt-2 font-geist text-sm text-leadrs-text-soft">
                        {`Gagnez ${formatEuro(commissionConfig.level1)} pour chaque lead acheté par les acheteurs inscrits avec votre lien.`}
                    </p>
                </div>
                <div className="pointer-events-none absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-leadrs-purple-soft to-transparent opacity-40" />
            </div>

            {/* KPI stats */}
            <div className="grid grid-cols-2 gap-5 xl:grid-cols-4">
                <MiniStatistics
                    name="Filleuls directs (N1)"
                    value={loading ? '-' : String(stats?.userCount ?? 0)}
                    icon={<MdOutlinePeopleOutline color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Filleuls indirects (N2)"
                    value={loading ? '-' : String(level2Stats?.userCount ?? 0)}
                    icon={<MdOutlinePeopleOutline color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Leads achetés (validés)"
                    value={loading ? '-' : String(totalLeads)}
                    icon={<TbCoins color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Commissions totales"
                    value={loading ? '-' : formatEuro(totalDue)}
                    icon={<MdOutlineEmojiEvents color="#1C0F53" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                {/* Referral link card */}
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-sm font-semibold text-leadrs-text">Votre lien de parrainage</p>
                    <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                        Partagez ce lien ou votre code pour inviter de nouveaux acheteurs.
                    </p>

                    {affiliateLink ? (
                        <>
                            <div className="mt-4 flex items-center justify-between rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                                <div>
                                    <p className="font-geist text-xs text-leadrs-text-soft">Votre code</p>
                                    <p className="font-geist text-lg font-bold tracking-widest text-leadrs-brand">
                                        {user?.affiliateCode}
                                    </p>
                                </div>
                                <button
                                    onClick={() => navigator.clipboard.writeText(user?.affiliateCode)}
                                    className="flex items-center gap-1.5 rounded-md border border-leadrs-stroke bg-white px-3 py-1.5 font-geist text-xs font-medium text-leadrs-text transition-colors hover:bg-leadrs-bg"
                                >
                                    <MdContentCopy className="text-sm" />
                                    Copier
                                </button>
                            </div>

                            <div className="mt-2 flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                                <span className="flex-1 truncate font-geist text-xs text-leadrs-text-soft">{affiliateLink}</span>
                                <button
                                    onClick={handleCopy}
                                    className="flex shrink-0 items-center gap-1.5 rounded-md bg-leadrs-brand px-3 py-1.5 font-geist text-xs font-semibold text-white transition-opacity hover:opacity-80"
                                >
                                    {copied ? (
                                        <>
                                            <MdCheck className="text-sm" />
                                            Copié !
                                        </>
                                    ) : (
                                        <>
                                            <MdContentCopy className="text-sm" />
                                            Copier le lien
                                        </>
                                    )}
                                </button>
                            </div>

                            <div className="mt-4">
                                <p className="mb-2 font-geist text-xs font-semibold uppercase tracking-wide text-leadrs-text-soft">
                                    Partager via
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <a
                                        href={`https://wa.me/?text=${encodeURIComponent(`J'achète mes leads sur Leadrs, inscrivez-vous avec mon lien : ${affiliateLink}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-green-soft px-3 py-2 font-geist text-xs font-semibold text-leadrs-green-focus transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandWhatsapp className="text-base" />
                                        WhatsApp
                                    </a>
                                    <a
                                        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(affiliateLink)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-blue-soft px-3 py-2 font-geist text-xs font-semibold text-leadrs-blue-focus transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandLinkedin className="text-base" />
                                        LinkedIn
                                    </a>
                                    <a
                                        href={`https://x.com/intent/tweet?text=${encodeURIComponent(`J'achète mes leads sur Leadrs, rejoignez-moi : ${affiliateLink}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-3 py-2 font-geist text-xs font-semibold text-leadrs-text transition-opacity hover:opacity-80"
                                    >
                                        <TbBrandTwitter className="text-base" />
                                        X / Twitter
                                    </a>
                                    <a
                                        href={`mailto:?subject=${encodeURIComponent('Rejoignez Leadrs')}&body=${encodeURIComponent(`Bonjour,\n\nJe vous invite à rejoindre Leadrs pour acheter des leads qualifiés.\n\nInscrivez-vous via mon lien : ${affiliateLink}\n\nÀ bientôt !`)}`}
                                        className="flex items-center gap-2 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-3 py-2 font-geist text-xs font-semibold text-leadrs-text transition-opacity hover:opacity-80"
                                    >
                                        <TbMail className="text-base" />
                                        Email
                                    </a>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="mt-4 rounded-lg border border-leadrs-stroke bg-leadrs-bg px-4 py-3">
                            <p className="font-geist text-xs text-leadrs-text-soft">
                                {userLoading ? 'Chargement...' : 'Lien indisponible.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Bonus amounts card */}
                <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                    <p className="font-geist text-sm font-semibold text-leadrs-text">Niveaux de parrainage</p>
                    <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                        Vous êtes récompensé sur deux niveaux pour chaque lead acheté par vos filleuls.
                    </p>
                    <div className="mt-4 flex flex-col gap-3">
                        {[
                            { level: 'Niveau 1', desc: 'Par lead acheté par un de vos filleuls directs', amount: commissionConfig.level1 },
                            { level: 'Niveau 2', desc: 'Par lead acheté par les filleuls de vos filleuls', amount: commissionConfig.level2 },
                        ].map((r) => (
                            <div key={r.level} className="flex items-center justify-between rounded-lg bg-leadrs-bg px-4 py-4">
                                <div>
                                    <p className="font-geist text-sm font-semibold text-leadrs-text">{r.level}</p>
                                    <p className="mt-0.5 font-geist text-xs text-leadrs-text-soft">{r.desc}</p>
                                </div>
                                <span className="ml-4 shrink-0 font-crimson text-2xl font-bold text-leadrs-brand">
                                    {formatEuro(r.amount)}
                                </span>
                            </div>
                        ))}
                    </div>
                    <p className="mt-4 font-geist text-xs text-leadrs-text-soft">
                        La commission est ajoutée à votre solde quand le lead est validé (après la période de contestation),
                        et vous pouvez la retirer par virement.
                    </p>
                </div>
            </div>

            {/* Referrals list */}
            <div className="rounded-xl border border-leadrs-stroke bg-white p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <p className="font-geist text-sm font-semibold text-leadrs-text">Mes filleuls</p>
                        <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                            {levelView === 'n1' ? 'Filleuls directs — Niveau 1' : 'Filleuls indirects — Niveau 2'}
                        </p>
                    </div>
                    <div className="flex rounded-lg border border-leadrs-stroke bg-leadrs-bg p-0.5">
                        {(['n1', 'n2'] as const).map((lvl) => (
                            <button
                                key={lvl}
                                onClick={() => setLevelView(lvl)}
                                className={`rounded-md px-3 py-1.5 font-geist text-xs font-semibold transition-colors ${levelView === lvl ? 'bg-white text-leadrs-text shadow-sm' : 'text-leadrs-text-soft hover:text-leadrs-text'}`}
                            >
                                {lvl === 'n1' ? 'Niveau 1' : 'Niveau 2'}
                            </button>
                        ))}
                    </div>
                </div>

                {!loading && rows.length === 0 ? (
                    <p className="mt-6 text-center font-geist text-sm text-leadrs-text-soft">
                        Aucun filleul pour le moment. Partagez votre lien pour commencer !
                    </p>
                ) : (
                    <div className="mt-4 overflow-x-auto">
                        <table className="w-full text-left font-geist text-sm">
                            <thead>
                                <tr className="border-b border-leadrs-stroke">
                                    <th className="py-2 pr-4 font-medium text-leadrs-text-soft">Acheteur</th>
                                    <th className="py-2 pr-4 text-right font-medium text-leadrs-text-soft">Leads achetés</th>
                                    <th className="py-2 pr-4 text-right font-medium text-leadrs-text-soft">Prix / lead</th>
                                    <th className="py-2 text-right font-medium text-leadrs-text-soft">Commission</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row._id} className="border-t border-leadrs-stroke hover:bg-leadrs-bg">
                                        <td className="py-3 pr-4">
                                            <p className="font-medium text-leadrs-text">{row.name || '—'}</p>
                                            <p className="text-xs text-leadrs-text-soft">{row.email}</p>
                                        </td>
                                        <td className="py-3 pr-4 text-right text-leadrs-text">{row.leadCount}</td>
                                        <td className="py-3 pr-4 text-right text-leadrs-text-soft">{formatEuro(row.pricePerLead)}</td>
                                        <td className="py-3 text-right font-semibold text-leadrs-green-focus">
                                            {row.totalDue > 0 ? formatEuro(row.totalDue) : '—'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Wallet payout CTA — same affiliate wallet and manual payout flow as sellers */}
            {!loading && walletBalance > 0 && (
                <div className={`rounded-xl border p-5 ${pendingRequest ? 'border-amber-200 bg-amber-50' : walletBalance >= 50 ? 'border-leadrs-green-soft bg-leadrs-green-soft' : 'border-leadrs-stroke bg-white'}`}>
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div>
                            <p className="font-geist text-xs font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                Solde disponible
                            </p>
                            <p className="mt-1 font-crimson text-3xl font-bold text-leadrs-brand">{formatEuro(walletBalance)}</p>
                            {pendingRequest ? (
                                <p className="mt-1 font-geist text-xs text-amber-600">
                                    Demande de virement en cours — {formatEuro(pendingRequest.amount)} demandés le{' '}
                                    {new Date(pendingRequest.requestedAt).toLocaleDateString('fr-FR')}
                                </p>
                            ) : walletBalance < 50 ? (
                                <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                    Minimum 50 € requis pour demander un virement ({formatEuro(50 - walletBalance)} manquants).
                                </p>
                            ) : !hasIban ? (
                                <p className="mt-1 font-geist text-xs text-leadrs-text-soft">
                                    Renseignez votre IBAN pour recevoir votre virement.
                                </p>
                            ) : (
                                <p className="mt-1 font-geist text-xs text-leadrs-green-focus">Votre solde est éligible au virement.</p>
                            )}
                            {paymentError && <p className="mt-1 font-geist text-xs text-leadrs-red-focus">{paymentError}</p>}
                        </div>
                        {!pendingRequest && walletBalance >= 50 && !hasIban && (
                            <button
                                onClick={() => navigate('/admin/settings?tab=invoice')}
                                className="flex shrink-0 items-center gap-1.5 rounded-lg bg-leadrs-brand px-5 py-2.5 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80"
                            >
                                Renseigner mon IBAN
                                <MdArrowForward className="h-4 w-4" />
                            </button>
                        )}
                        {!pendingRequest && walletBalance >= 50 && hasIban && (
                            <button
                                onClick={requestPayment}
                                disabled={paymentLoading}
                                className="flex shrink-0 items-center gap-2 rounded-lg bg-leadrs-brand px-5 py-2.5 font-geist text-sm font-semibold text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                            >
                                {paymentLoading ? 'Envoi...' : 'Demander mon virement'}
                            </button>
                        )}
                        {pendingRequest && (
                            <span className="flex shrink-0 items-center gap-2 rounded-lg border border-amber-300 bg-amber-100 px-4 py-2 font-geist text-sm font-semibold text-amber-700">
                                En attente de traitement
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BuyerAffiliate;
